import React, { useEffect, useState } from 'react';
import { HelpCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useProductTour } from '../../contexts/ProductTourContext';
import { usePageHelpAvailable } from '../../learnerTour/pageHelpAvailability';
import { useAppLocale } from '../../providers/LocaleProvider';

export const PAGE_TOUR_REQUEST_EVENT = 'app:requestPageTour';

export function requestModalPageTour(pageKey?: string): void {
  window.dispatchEvent(
    new CustomEvent(PAGE_TOUR_REQUEST_EVENT, {
      detail: pageKey ? { pageKey } : undefined,
    })
  );
}

/**
 * Compact page-guide trigger for slide modals — sits directly under the close (X) control.
 */
export const ModalTourHelpButton: React.FC<{
  className?: string;
  tourPageKey?: string;
}> = ({ className = '', tourPageKey }) => {
  const { t } = useTranslation('tour');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const { isRunning: isTourRunning } = useProductTour();
  const helpForKey = usePageHelpAvailable(tourPageKey ?? null);
  const pageHelpAvailable = tourPageKey ? helpForKey : true;
  const [tourHelpLoading, setTourHelpLoading] = useState(false);

  useEffect(() => {
    if (isTourRunning && tourHelpLoading) {
      setTourHelpLoading(false);
    }
  }, [isTourRunning, tourHelpLoading]);

  useEffect(() => {
    const stopLoading = () => setTourHelpLoading(false);
    window.addEventListener('app:productTourRunning', stopLoading);
    window.addEventListener('app:productTourHelpSettled', stopLoading);
    return () => {
      window.removeEventListener('app:productTourRunning', stopLoading);
      window.removeEventListener('app:productTourHelpSettled', stopLoading);
    };
  }, []);

  useEffect(() => {
    if (!tourHelpLoading) return undefined;
    const timer = window.setTimeout(() => setTourHelpLoading(false), 8000);
    return () => window.clearTimeout(timer);
  }, [tourHelpLoading]);

  const handleClick = () => {
    if (!pageHelpAvailable || tourHelpLoading || isTourRunning) return;
    setTourHelpLoading(true);
    requestModalPageTour(tourPageKey);
  };

  const helpLabel = pageHelpAvailable
    ? t('pageHelp')
    : t('pageHelpUnavailable', {
        defaultValue: isRtl ? 'برای این صفحه راهنمایی نیست' : 'No guide on this page',
      });

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!pageHelpAvailable || tourHelpLoading}
      className={`flex h-5 w-5 items-center justify-center rounded-full text-teal-700 transition hover:bg-teal-50/80 disabled:pointer-events-none disabled:opacity-40 dark:text-teal-300 dark:hover:bg-slate-700/60 ${className}`}
      aria-label={helpLabel}
      title={helpLabel}
      aria-busy={tourHelpLoading}
      aria-disabled={!pageHelpAvailable || undefined}
    >
      {tourHelpLoading ? (
        <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} aria-hidden />
      ) : (
        <HelpCircle className="h-3 w-3" strokeWidth={2} aria-hidden />
      )}
    </button>
  );
};
